import { useCallback, useMemo } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';

import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { OptionSelect } from '../../../shared/ui/forms/OptionSelect';
import { FieldPair } from '../../../shared/ui/forms/FieldPair';
import { useMessages } from '../../../shared/i18n/useMessages';
import { SUB_QUESTIONS_BUNDLE, type SubKey } from './subQuestionsMessages';
import {
  RESPONSE_TYPE_CHOICES,
  SHOW_ON_CHOICES,
  toSelectItems,
} from './responseTypeOptions';
import { ResponseType, ShowOn } from '../../../../types/helios/Question';

export interface SubQuestionResponseTypePickerProps {
  /** Current response type value (the local enum string). */
  readonly responseType: string;
  readonly onResponseTypeChange: (value: string) => void;
  /** Whether the parent's conditional display is ON. The trigger selector is
   *  ALWAYS rendered beside the response type (matching v1), but is DISABLED
   *  when this is false — a sub always shows until conditional display is on. */
  readonly conditionalEnabled: boolean;
  /** Current `showWhenParentIs` value; defaults to BOTH in the selector. */
  readonly showWhenParentIs?: string | undefined;
  readonly onShowOnChange: (value: string) => void;
  /** When true, Pass/Fail is removed from the type options — a scored sub
   *  can't exist under conditional display (mirrors the manual flow). */
  readonly excludePassFail: boolean;
  readonly disabled: boolean;
  readonly dataTestId?: string;
}

/**
 * The response-type dropdown for a sub-question, plus the conditional
 * trigger selector (shown when parent is Pass/Fail/Both) when applicable.
 * Extracted from
 * {@link SubQuestionRow} so the row stays under the file-size limit and the
 * two label+select clusters live in one testable place.
 *
 * Both selects' option lists are memoised (OptionSelect compares the selected
 * option by reference). `t` is ref-stable, so `isBundleLoading` is in the deps
 * to force a rebuild once labels resolve (the useMemo([t]) trap).
 */
export function SubQuestionResponseTypePicker({
  responseType,
  onResponseTypeChange,
  conditionalEnabled,
  showWhenParentIs,
  onShowOnChange,
  excludePassFail,
  disabled,
  dataTestId,
}: SubQuestionResponseTypePickerProps): JSX.Element {
  const { t, isLoading: isBundleLoading } = useMessages<SubKey>(SUB_QUESTIONS_BUNDLE);

  const responseTypeItems = useMemo(
    () => {
      // `choice.value` is a plain string; compare against the enum's string
      // value to avoid the cross-enum-comparison lint.
      const passFail: string = ResponseType.PASS_FAIL;
      const choices = excludePassFail
        ? RESPONSE_TYPE_CHOICES.filter((choice) => choice.value !== passFail)
        : RESPONSE_TYPE_CHOICES;
      return toSelectItems(choices, t);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is ref-stable; isBundleLoading forces a rebuild once labels resolve (useMemo([t]) trap)
    [t, isBundleLoading, excludePassFail],
  );
  const showOnItems = useMemo(
    () => toSelectItems(SHOW_ON_CHOICES, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see responseTypeItems
    [t, isBundleLoading],
  );

  const handleShowOn = useCallback(
    (value: string) => onShowOnChange(value),
    [onShowOnChange],
  );

  // Hold the selects until the i18n bundle resolves. OptionSelect renders the
  // SELECTED option's label via renderSelectedValue and caches it by option
  // reference; if the first paint happens with the raw-key labels (bundle not
  // yet resolved), the trigger shows the raw key (e.g. "subResponseTypePassFail")
  // until the value changes. Gating on !isBundleLoading — the same guard the
  // RichTextEditor uses in this feature — guarantees real labels on first paint.
  if (isBundleLoading) return <Col gridGap="dimensions.spacing.300" width="100%" />;

  // Resolved once each — reused as both the visible label and the select's
  // accessible name (the scanner flags a `t(key)` used twice as a duplicate).
  const responseTypeLabel = t('subResponseTypeLabel');
  const showOnLabel = t('subShowOnLabel');

  return (
    // Response type | Show-when-parent-is, side by side (same FieldPair the
    // Question Configuration step uses for BU | Division). The show-on selector
    // is always rendered (matching v1), disabled until conditional display is on.
    <FieldPair
      left={
        <Col gridGap="dimensions.spacing.100">
          <RequiredLabel isRequired hasError={false}>
            {responseTypeLabel}
          </RequiredLabel>
          <OptionSelect
            options={responseTypeItems}
            value={responseType}
            onChange={onResponseTypeChange}
            disabled={disabled}
            ariaLabel={responseTypeLabel}
            {...(dataTestId !== undefined
              ? { dataTestId: `${dataTestId}-response-type` }
              : {})}
          />
        </Col>
      }
      right={
        <Col gridGap="dimensions.spacing.100">
          <RequiredLabel isRequired hasError={false}>
            {showOnLabel}
          </RequiredLabel>
          <OptionSelect
            options={showOnItems}
            value={showWhenParentIs ?? ShowOn.BOTH}
            onChange={handleShowOn}
            disabled={disabled || !conditionalEnabled}
            ariaLabel={showOnLabel}
            {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-show-on` } : {})}
          />
        </Col>
      }
    />
  );
}
